import styled from 'styled-components';

import DrfqTakerOrderbookProvider, {
  useDrfqTakerOrderbook,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-taker-orderbook-context';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import useDrfqBbo from '@paradigm/features/src/unified-rfqs/contexts/use-drfq-bbo';
import { formatPrecision } from '@paradigm/utils/formatDecimal';
import { nepal } from '@paradigm/design-system/src/colors';
import { BboLeg } from '@paradigm/features/src/unified-rfqs/entities/drfq/bbo';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';

import {
  LegsTableContainer,
  Row,
  TableHeader,
} from '#/components/rfq-common/LegsTable';
import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import {
  DateInfoMobile,
  LeftInfo,
  MobileRfqInfo,
} from '#/components/UnifiedRfqs/components/DetailsModal';
import Grid from '#/components/UnifiedRfqs/components/Grid';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { LegsContainer } from '#/components/UnifiedRfqs/components/MobileLegsTable';
import {
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';
import StrategyGreeks from '#/components/UnifiedRfqs/components/StrategyGreeks';
import { RightContainer } from '#/components/UnifiedRfqs/grfq/quote-builder/QuoteBuilder';
import MarkPrice from '#/components/UnifiedRfqs/components/MarkPrice';
import RfqDescription from '#/components/UnifiedRfqs/drfq/RfqDescription';
import DrfqDetailsLegsTable from '#/components/UnifiedRfqs/drfq/taker-rfq-details/DrfqDetailsLegsTable';
import CancelDrfqButton from '#/components/UnifiedRfqs/drfq/taker-rfq-details/CancelDrfqButton';
import { OrderbookDetailsModal } from '#/components/UnifiedRfqs/drfq/taker-rfq-details/OrderbookDetailsModal';
import {
  AnonMessage,
  WarningMessage,
} from '#/components/UnifiedRfqs/components/WarningMessage';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { TrackingCtxProvider } from '@paradigm/features/src/analytics';

interface Props {
  readonly close: () => void;
  readonly isOpen: boolean;
}

export default function DrfqDetailsModal() {
  const { isItemDetailsOpen } = useContextMenuView();
  const { closeDetailsModal } = useContextMenuActions();

  return (
    <TrackingCtxProvider value="rfq details">
      <DrfqTakerOrderbookProvider>
        <DrfqDetailsModalContent
          isOpen={isItemDetailsOpen}
          close={closeDetailsModal}
        />
      </DrfqTakerOrderbookProvider>
    </TrackingCtxProvider>
  );
}

function DrfqDetailsModalContent({ isOpen, close }: Props) {
  const drfq = useDrfqRfq();
  const isMobile = useIsMobile();
  const { legs } = drfq;
  const view = useDrfqTakerOrderbook();
  const displayOrderbook =
    view.state === 'OPEN_AUCTION' &&
    (view.asks.length > 0 || view.bids.length > 0);
  const bbo = useBboView().data;
  const regularLegs = legs.filter((leg) => !leg.isHedge);
  const isSingleLeg = regularLegs.length === 1;

  const isAnonRFQ = drfq.is_taker_anonymous;

  const modalWidth = isSingleLeg ? 975 : 1042;
  const rightPanel = (
    <RightContainer>
      <StrategyGreeks
        css="justify-content: start;"
        venue={drfq.venue}
        bbo={bbo}
      />
    </RightContainer>
  );
  const desktopContent = (
    <Container>
      <div>
        <Grid cols={1} css="padding: 1rem;">
          <RfqDescription drfq={drfq} isExecuted={false} />
          <DetailsLegsTableContainer isSingleLeg={isSingleLeg}>
            <DrfqDetailsLegsTable drfq={drfq} />
            {!isSingleLeg && <MarkPriceLegs drfq={drfq} />}
          </DetailsLegsTableContainer>
          {isAnonRFQ && <AnonWarningMessage />}
        </Grid>
        {displayOrderbook && <OrderbookDetailsModal />}
        <Footer>
          <CancelDrfqButton rfqId={drfq.id} onCanceled={close} />
        </Footer>
      </div>
      {rightPanel}
    </Container>
  );
  const mobileContent = (
    <MobileContainer>
      <div>
        <MobileRfqInfo
          venue={drfq.venue}
          productCode={drfq.product_codes[0]}
          description={drfq.description}
        />
        <Grid cols={1} css="padding: 1rem;">
          <div css="margin: auto">
            <MarkPrice />
          </div>
          <LegsContainer>
            <DrfqDetailsLegsTable drfq={drfq} />
          </LegsContainer>
          {isAnonRFQ && <AnonWarningMessage />}
        </Grid>
        {displayOrderbook && <OrderbookDetailsModal />}
      </div>
      <Grid cols={1} css="padding: 1rem;">
        {rightPanel}
      </Grid>
      <Footer>
        <CancelDrfqButton rfqId={drfq.id} onCanceled={close} />
      </Footer>
    </MobileContainer>
  );
  return (
    <HeadedModal
      title="Details"
      isOpen={isOpen}
      close={close}
      theme={isMobile ? 'primary-light' : 'default'}
      leftInfo={
        isMobile ? (
          <DateInfoMobile>
            <p>{hourFormat(drfq.last_updated_at)}</p>
            <p>{shortDateFormat(drfq.last_updated_at)}</p>
          </DateInfoMobile>
        ) : (
          <LeftInfo venue={drfq.venue} productCode={drfq.product_codes[0]} />
        )
      }
      width={modalWidth}
      aria-label="unified-rfq rfq details modal"
    >
      {isMobile ? mobileContent : desktopContent}
    </HeadedModal>
  );
}

const AnonWarningMessage = () => {
  return (
    <AnonMessageContainer>
      <WarningMessage>
        <AnonMessage />
      </WarningMessage>
    </AnonMessageContainer>
  );
};

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;

function MarkPriceLegs({ drfq }: { drfq: Drfq }) {
  const bbo = useDrfqBbo(drfq.id);
  return (
    <LegsTableContainer css="padding-top: 42px;">
      <TableHeader>
        <MarkPriceRow>
          <span>Mark Price</span>
        </MarkPriceRow>
      </TableHeader>
      {bbo?.legs.map((leg) => (
        <MarkPriceRowValue key={`${drfq.id}-${leg.instrument_id}`} leg={leg} />
      ))}
    </LegsTableContainer>
  );
}
interface MarkPriceRowValueProps {
  readonly leg: BboLeg;
}
function MarkPriceRowValue({ leg }: MarkPriceRowValueProps) {
  const { mark_price, mark_price_iv } = leg;

  return (
    <MarkPriceRowIv>
      {mark_price === '' ? NEGATIVE_SIGN : formatPrecision(mark_price, 4)}
      {mark_price_iv !== null && <MarkPriceIv>{mark_price_iv}%</MarkPriceIv>}
    </MarkPriceRowIv>
  );
}

const AnonMessageContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

export const MarkPriceRow = styled(Row)`
  grid-template-columns: auto;
`;

export const MarkPriceIv = styled.span`
  opacity: 0.3;
  font-size: 10px;
  color: #ffffff;
`;

export const MarkPriceRowIv = styled.div`
  color: ${nepal};
  display: flex;
  padding: 5px 0 0 12px;
  height: 42px;
  align-items: start;
  flex-direction: column;
`;

export const InputPriceRow = styled(Row)`
  grid-template-columns: auto auto;
`;

const DetailsLegsTableContainer = styled.div<{ isSingleLeg: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.isSingleLeg ? '100% 0%' : '85% 15%'};
  gap: 5px;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 232px;

  ${RightContainer} {
    align-content: start;
  }
`;
const MobileContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  ${RightContainer} {
    display: none;
    width: 100%;
    border-radius: 0;
  }

  .mark-container {
    margin: 0 auto;
    padding-top: 0.375rem;

    span {
      font-size: 0.875rem;
      line-height: 1rem;
    }
  }

  .mark-row-container {
    grid-template-columns: 2rem auto;
  }

  &&& .strategy-greeks {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > * {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  &&& .strategy-greeks-cell {
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1rem;
  }

  .strategy-greeks-title {
    font-size: 1rem;
    line-height: 1.25rem;
    font-weight: normal;
    padding: 0 0 0.5rem;
  }
`;
